import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Star, StarOff, ExternalLink, Eye, EyeOff } from "lucide-react";
import {
  authMe, authLogout,
  adminListInterviews, createInterview, updateInterview, deleteInterview,
  getCategories,
} from "../lib/api";

const EMPTY = {
  name: "", profession: "", specialty: "", organization: "",
  category: "Physicians", biography: "", profile_image: "", video_url: "", audio_url: "",
  transcript: "", career_journey: "", advice: "", insights: "", research: "", excerpt: "",
  essay: "",
  featured: false, published: false,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // interview or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    if (user) { setChecking(false); return; }
    authMe().then((u) => { setUser(u); setChecking(false); })
      .catch(() => { navigate("/admin/login", { replace: true }); });
  }, [navigate, user]);

  const reload = () => adminListInterviews().then(setItems).catch(() => {});

  useEffect(() => {
    if (!user) return;
    reload();
    getCategories().then(setCategories).catch(() => {});
  }, [user]);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const startEdit = (iv) => { setEditing(iv); setForm({ ...EMPTY, ...iv }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await updateInterview(editing.id, form);
        toast.success("Interview updated");
      } else {
        await createInterview(form);
        toast.success("Interview created");
      }
      closeForm();
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (iv) => {
    if (!window.confirm(`Delete "${iv.name}"?`)) return;
    try {
      await deleteInterview(iv.id);
      toast.success("Deleted");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  const toggleFeatured = async (iv) => {
    try {
      await updateInterview(iv.id, { featured: !iv.featured });
      reload();
    } catch { toast.error("Update failed"); }
  };

  const togglePublished = async (iv) => {
    try {
      await updateInterview(iv.id, { published: !iv.published });
      toast.success(iv.published ? "Moved to drafts" : "Published — now live");
      reload();
    } catch { toast.error("Update failed"); }
  };

  const logout = async () => {
    await authLogout();
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-navy/60">Verifying session…</div>;
  }

  return (
    <div className="min-h-screen bg-parchment" data-testid="admin-dashboard">
      {/* Admin header */}
      <header className="bg-white border-b border-rule">
        <div className="container-wide h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif text-xl text-navy">Tomorrow&rsquo;s Doctors</Link>
            <span className="label-eyebrow">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/inbox" className="text-xs uppercase tracking-[0.2em] text-navy/70 hover:text-navy inline-flex items-center gap-1" data-testid="admin-inbox-link">
              Inbox
            </Link>
            {user && (
              <div className="hidden sm:flex items-center gap-3 text-sm text-navy/70">
                {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
                <span data-testid="admin-user-email">{user.email}</span>
              </div>
            )}
            <button onClick={logout} className="text-xs uppercase tracking-[0.2em] text-navy/70 hover:text-navy inline-flex items-center gap-1" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-wide py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-eyebrow mb-2">Interviews</div>
            <h1 className="font-serif text-4xl text-navy">Manage the archive.</h1>
          </div>
          <button onClick={startCreate} className="btn-primary" data-testid="new-interview-btn">
            <Plus size={16} className="mr-2" /> New Interview
          </button>
        </div>

        <div className="bg-white border border-rule overflow-hidden" data-testid="interviews-table">
          <table className="w-full text-sm">
            <thead className="bg-parchment border-b border-rule">
              <tr className="text-left text-xs uppercase tracking-[0.15em] text-navy/60">
                <th className="p-4">Name</th>
                <th className="p-4 hidden md:table-cell">Category</th>
                <th className="p-4 hidden lg:table-cell">Organization</th>
                <th className="p-4 text-center">Status</th>
                <th className="p-4 text-center">Featured</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="p-12 text-center text-navy/60">No interviews yet — create your first.</td></tr>
              ) : items.map((iv) => (
                <tr key={iv.id} className="border-b border-rule last:border-0 hover:bg-parchment/50" data-testid={`row-${iv.id}`}>
                  <td className="p-4">
                    <div className="font-serif text-lg text-navy">{iv.name}</div>
                    <div className="text-xs text-navy/60">{iv.profession}{iv.specialty ? ` · ${iv.specialty}` : ""}</div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-navy/70">{iv.category}</td>
                  <td className="p-4 hidden lg:table-cell text-navy/70">{iv.organization}</td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => togglePublished(iv)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.18em] border transition-colors ${
                        iv.published
                          ? "bg-medgreen/10 text-medgreen border-medgreen/30 hover:bg-medgreen/20"
                          : "bg-parchment text-navy/60 border-rule hover:border-navy"
                      }`}
                      data-testid={`published-${iv.id}`}
                      title={iv.published ? "Live — click to unpublish" : "Draft — click to publish"}
                    >
                      {iv.published ? <><Eye size={11} /> Live</> : <><EyeOff size={11} /> Draft</>}
                    </button>
                  </td>
                  <td className="p-4 text-center">
                    <button onClick={() => toggleFeatured(iv)} data-testid={`featured-${iv.id}`}>
                      {iv.featured ? <Star size={18} className="text-medblue fill-medblue" /> : <StarOff size={18} className="text-navy/40" />}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/interviews/${iv.id}`} target="_blank" className="p-2 text-navy/70 hover:text-medblue" title="View"><ExternalLink size={16} /></Link>
                      <button onClick={() => startEdit(iv)} className="p-2 text-navy/70 hover:text-medblue" data-testid={`edit-${iv.id}`}><Pencil size={16} /></button>
                      <button onClick={() => remove(iv)} className="p-2 text-navy/70 hover:text-destructive" data-testid={`delete-${iv.id}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-start justify-center overflow-auto py-10" data-testid="interview-form-modal" onClick={closeForm}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-rule w-full max-w-3xl mx-4 p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">{editing ? "Edit" : "Create"}</div>
                <h2 className="font-serif text-2xl text-navy">{editing ? "Edit interview" : "New interview"}</h2>
              </div>
              <button type="button" onClick={closeForm} className="text-navy/60 hover:text-navy">Close</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Name" value={form.name} required onChange={(v) => setForm({ ...form, name: v })} testid="input-name" />
              <Input label="Profession / Title" value={form.profession} required onChange={(v) => setForm({ ...form, profession: v })} testid="input-profession" />
              <Input label="Specialty" value={form.specialty} onChange={(v) => setForm({ ...form, specialty: v })} testid="input-specialty" />
              <Input label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="input-organization" />
              <div>
                <Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-rule px-3 py-2 bg-white" data-testid="select-category">
                  {(categories.length ? categories : ["Physicians"]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-end gap-6">
                <label className="inline-flex items-center gap-2 text-sm text-navy">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="input-published" />
                  Publish (visible to public)
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-navy">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="input-featured" />
                  Featured on homepage
                </label>
              </div>
              <Input label="Profile Image URL" value={form.profile_image} onChange={(v) => setForm({ ...form, profile_image: v })} testid="input-image" className="md:col-span-2" />
              <Input label="Video URL (embed)" value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} testid="input-video" />
              <Input label="Audio URL" value={form.audio_url} onChange={(v) => setForm({ ...form, audio_url: v })} testid="input-audio" />
              <TextArea label="Short excerpt" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} testid="input-excerpt" rows={2} className="md:col-span-2" />
              <TextArea label="Biography (short, shown near photo)" value={form.biography} onChange={(v) => setForm({ ...form, biography: v })} testid="input-bio" rows={3} className="md:col-span-2" />
              <TextArea label="Essay (main editorial write-up). Use `## Heading` for takeaway titles; blank lines separate paragraphs. First block (no heading) becomes the About intro." value={form.essay} onChange={(v) => setForm({ ...form, essay: v })} testid="input-essay" rows={10} className="md:col-span-2" />
              <TextArea label="Career Journey (optional — leave blank if using Essay)" value={form.career_journey} onChange={(v) => setForm({ ...form, career_journey: v })} testid="input-journey" rows={4} className="md:col-span-2" />
              <TextArea label="Student Advice (optional)" value={form.advice} onChange={(v) => setForm({ ...form, advice: v })} testid="input-advice" rows={4} className="md:col-span-2" />
              <TextArea label="Healthcare Insights (optional)" value={form.insights} onChange={(v) => setForm({ ...form, insights: v })} testid="input-insights" rows={4} className="md:col-span-2" />
              <TextArea label="Research / Innovation (optional)" value={form.research} onChange={(v) => setForm({ ...form, research: v })} testid="input-research" rows={4} className="md:col-span-2" />
              <TextArea label="Full Transcript" value={form.transcript} onChange={(v) => setForm({ ...form, transcript: v })} testid="input-transcript" rows={8} className="md:col-span-2" />
            </div>
            <div className="mt-8 flex items-center justify-end gap-3">
              <button type="button" onClick={closeForm} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="save-interview">
                {saving ? "Saving…" : (editing ? "Save changes" : "Create interview")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => <label className="block text-[11px] uppercase tracking-[0.18em] text-navy/60 mb-1.5">{children}</label>;
const Input = ({ label, value, onChange, required, testid, className = "" }) => (
  <div className={className}>
    <Label>{label}{required && " *"}</Label>
    <input required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-rule focus:border-navy outline-none px-3 py-2 bg-white text-sm" data-testid={testid} />
  </div>
);
const TextArea = ({ label, value, onChange, testid, rows = 3, className = "" }) => (
  <div className={className}>
    <Label>{label}</Label>
    <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-rule focus:border-navy outline-none px-3 py-2 bg-white text-sm leading-relaxed font-sans" data-testid={testid} />
  </div>
);
